import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import {
  initialState,
  type CompanyDetailsApiResponse,
  type Country,
  type CountryState,
  type ProductCategory,
  type SubmitOnboardingArgs,
  type Term,
} from "./types/onboard.types";
import type { IHttpError, RootState } from "Types";
import {
  addProgressAction,
  isActionInProgressSelector,
  removeProgressAction,
} from "../utils/loader/progress.slice";
import { httpGet, httpPost } from "../services/api/axiosHelpers";
import { accountUrls, onboardUrls } from "../services/api/api-urls";
import { handleAPIError } from "../utils/error.util";
import { toPostData } from "../utils";

export const fetchSourceCountries = createAsyncThunk<
  Country[],
  { useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "onboarding/fetchSourceCountries",
  async ({ useDummyToken, _signal }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "fetchSourceCountries" }));
      const response = await httpGet({
        useDummyToken,
        url: onboardUrls.sourceCountries,
        signal: _signal || signal,
      });
      return response.data;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "onboarding.slice.ts - fetchSourceCountries",
      });
    } finally {
      dispatch(removeProgressAction("fetchSourceCountries"));
    }
  },
);

export const fetchCountries = createAsyncThunk<
  Country[],
  { useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "onboarding/fetchCountries",
  async ({ useDummyToken, _signal }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "fetchCountries" }));
      const response = await httpGet({
        useDummyToken,
        url: onboardUrls.countries,
        signal: _signal || signal,
      });
      return response.data;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "onboarding.slice.ts - fetchCountries",
      });
    } finally {
      dispatch(removeProgressAction("fetchCountries"));
    }
  },
);

export const fetchDestinationCountries = createAsyncThunk<
  Country[],
  { useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "onboarding/fetchDestinationCountries",
  async ({ useDummyToken, _signal }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "fetchDestinationCountries" }));
      const response = await httpGet({
        useDummyToken,
        url: onboardUrls.destinationCountries,
        queryParams: { isInternationalShipping: true }, // international countries
        signal: _signal || signal,
      });
      return response.data;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "onboarding.slice.ts - fetchDestinationCountries",
      });
    } finally {
      dispatch(removeProgressAction("fetchDestinationCountries"));
    }
  },
);

export const fetchStates = createAsyncThunk<
  CountryState[],
  { country_code?: string; useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "onboarding/fetchStates",
  async (
    { country_code, useDummyToken, _signal },
    { dispatch, rejectWithValue, signal },
  ) => {
    try {
      dispatch(addProgressAction({ action: "fetchStates" }));
      const response = await httpGet({
        useDummyToken,
        url: onboardUrls.states,
        queryParams: { country_code },
        signal: _signal || signal,
      });
      return response.data;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "onboarding.slice.ts - fetchStates",
      });
    } finally {
      dispatch(removeProgressAction("fetchStates"));
    }
  },
);

export const fetchProductCategories = createAsyncThunk<
  ProductCategory[],
  { useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "onboarding/fetchProductCategories",
  async ({ useDummyToken, _signal }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "fetchProductCategories" }));
      const response = await httpGet({
        useDummyToken,
        url: onboardUrls.productCategories,
        signal: _signal || signal,
      });
      return response.data;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "onboarding.slice.ts - fetchProductCategories",
      });
    } finally {
      dispatch(removeProgressAction("fetchProductCategories"));
    }
  },
);

export const fetchTermsAndConditions = createAsyncThunk<
  Term[],
  { useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "onboarding/fetchTermsAndConditions",
  async ({ useDummyToken, _signal }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "fetchTermsAndConditions" }));
      const response = await httpGet({
        useDummyToken,
        url: onboardUrls.termsAndConditions,
        signal: _signal || signal,
      });
      return response.data;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "onboarding.slice.ts - fetchTermsAndConditions",
      });
    } finally {
      dispatch(removeProgressAction("fetchTermsAndConditions"));
    }
  },
);

export const fetchCompanyDetails = createAsyncThunk<
  CompanyDetailsApiResponse,
  { useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "onboarding/fetchCompanyDetails",
  async ({ useDummyToken, _signal }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "fetchCompanyDetails" }));
      const response = await httpGet({
        useDummyToken,
        url: onboardUrls.companyDetails,
        signal: _signal || signal,
      });
      return response.data;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "onboarding.slice.ts - fetchCompanyDetails",
      });
    } finally {
      dispatch(removeProgressAction("fetchCompanyDetails"));
    }
  },
);

export const submitOnboarding = createAsyncThunk<
  boolean,
  SubmitOnboardingArgs,
  {
    rejectValue: IHttpError;
  }
>(
  "onboarding/submitOnboarding",
  async (
    { payload, useDummyToken, _signal },
    { rejectWithValue, dispatch, signal },
  ) => {
    try {
      dispatch(addProgressAction({ action: "submitOnboarding" }));
      const response = await httpPost({
        useDummyToken,
        url: accountUrls.account,
        data: toPostData(payload),
        signal: _signal || signal,
      });
      if (response?.data.status === "SUCCESS") {
        return true;
      }
      return false;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "onboarding.slice.ts - submitOnboarding",
      });
    } finally {
      dispatch(removeProgressAction("submitOnboarding"));
    }
  },
);

export const fetchFormMasterData = createAsyncThunk<
  boolean,
  { useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "onboarding/fetchFormMasterData",
  async ({ useDummyToken, _signal }, { dispatch }) => {
    dispatch(addProgressAction({ action: "fetchFormMasterData" }));
    await Promise.allSettled([
      dispatch(fetchSourceCountries({ useDummyToken, _signal })),
      dispatch(fetchDestinationCountries({ useDummyToken, _signal })),
      dispatch(fetchCountries({ useDummyToken, _signal })),
      dispatch(fetchProductCategories({ useDummyToken, _signal })),
      dispatch(fetchTermsAndConditions({ useDummyToken, _signal })),
    ]);
    dispatch(removeProgressAction("fetchFormMasterData"));

    return true;
  },
);

export const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    setLastFocusTermInfo: (state, action) => {
      state.terms.lastActiveFocusTerm = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSourceCountries.pending, (state) => {
        state.sourceCountries.status = "loading";
      })
      .addCase(fetchSourceCountries.fulfilled, (state, action) => {
        state.sourceCountries.status = "idle";
        state.sourceCountries.data = action.payload;
      })
      .addCase(fetchSourceCountries.rejected, (state, action) => {
        state.sourceCountries.status = "failed";
        state.sourceCountries.error = action.payload || action.error;
      })

      .addCase(fetchDestinationCountries.pending, (state) => {
        state.destinationCountries.status = "loading";
      })
      .addCase(fetchDestinationCountries.fulfilled, (state, action) => {
        state.destinationCountries.status = "idle";
        state.destinationCountries.data = action.payload;
      })
      .addCase(fetchDestinationCountries.rejected, (state, action) => {
        state.destinationCountries.status = "failed";
        state.destinationCountries.error = action.payload || action.error;
      })

      .addCase(fetchProductCategories.pending, (state) => {
        state.productCategories.status = "loading";
      })
      .addCase(fetchProductCategories.fulfilled, (state, action) => {
        state.productCategories.status = "idle";
        state.productCategories.data = action.payload;
      })
      .addCase(fetchProductCategories.rejected, (state, action) => {
        state.productCategories.status = "failed";
        state.productCategories.error = action.payload || action.error;
      })

      .addCase(fetchTermsAndConditions.pending, (state) => {
        state.terms.status = "loading";
      })
      .addCase(fetchTermsAndConditions.fulfilled, (state, action) => {
        state.terms.status = "idle";
        state.terms.data = action.payload;
      })
      .addCase(fetchTermsAndConditions.rejected, (state, action) => {
        state.terms.status = "failed";
        state.terms.error = action.payload || action.error;
      })
      .addCase(fetchCompanyDetails.pending, (state) => {
        state.companyDetails.status = "loading";
      })
      .addCase(fetchCompanyDetails.fulfilled, (state, action) => {
        state.companyDetails.status = "idle";
        state.companyDetails.data = action.payload;
      })
      .addCase(fetchCompanyDetails.rejected, (state, action) => {
        state.companyDetails.status = "failed";
        state.companyDetails.error = action.payload || action.error;
      })
      .addCase(fetchStates.pending, (state) => {
        state.states.status = "loading";
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.states.status = "idle";
        state.states.data = action.payload;
      })
      .addCase(fetchStates.rejected, (state, action) => {
        state.states.status = "failed";
        state.states.error = action.payload || action.error;
      })
      .addCase(fetchCountries.pending, (state) => {
        state.countries.status = "loading";
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.countries.status = "idle";
        state.countries.data = action.payload;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.countries.status = "failed";
        state.countries.error = action.payload || action.error;
      });
  },
});

export const selectOnboarding = (state: RootState) => state.onboarding;

export const selectSourceCountries = createSelector(
  [selectOnboarding],
  (onboarding) => onboarding.sourceCountries,
);
export const selectSourceCountriesData = createSelector(
  [selectSourceCountries],
  (sourceCountries) => sourceCountries.data,
);

export const selectCountries = createSelector(
  [selectOnboarding],
  (onboarding) => onboarding.countries,
);

export const selectCountriesData = createSelector(
  [selectCountries],
  (countries) => countries.data,
);

export const selectStatesByCountryCode = createSelector(
  [
    selectCountriesData,
    (_state: RootState, countryCode?: string | null) => countryCode,
  ],
  (
    countriesData: Country[] | null,
    countryCode?: string | null,
  ): CountryState[] => {
    if (!countryCode) {
      return [];
    }

    const normalizedCountryCode = countryCode.toLowerCase();
    const matchedCountry = countriesData?.find(
      (country: Country) =>
        country.code.toLowerCase() === normalizedCountryCode,
    );

    return matchedCountry?.states ?? [];
  },
);

export const selectStatesData = createSelector(
  [selectOnboarding],
  (onboarding) => onboarding.states.data,
);

export const selectDestinationCountriesData = createSelector(
  [selectOnboarding],
  (onboarding) => onboarding.destinationCountries.data,
);

export const selectDestinationCountries = createSelector(
  [selectDestinationCountriesData],
  (destinationCountriesData) =>
    (destinationCountriesData ?? []).map(
      (item: { name: string; code: string }) => ({
        label: item.name,
        value: String(item.code),
      }),
    ),
);

export const selectProductCategoriesData = createSelector(
  [selectOnboarding],
  (onboarding) => onboarding.productCategories.data,
);

export const selectProductCategoryOptions = createSelector(
  [selectProductCategoriesData],
  (productCategoriesData) => {
    return (productCategoriesData ?? []).map(
      (item: { name: string; code: string }) => ({
        label: item.name,
        value: String(item.code),
      }),
    );
  },
);
export const selectTerms = createSelector([selectOnboarding], (onboarding) => {
  return onboarding.terms.data ?? [];
});

export const selectCompanyDetails = createSelector(
  [selectOnboarding],
  (onboarding) => onboarding.companyDetails.data,
);

export const selectLastFocusTerm = (state: RootState) =>
  state.onboarding.terms.lastActiveFocusTerm;

export const isSubmitOnboardingInProgressSelector = (state: RootState) =>
  isActionInProgressSelector(state, "submitOnboarding");

export const isFetchDestinationCountriesInProgressSelector = (
  state: RootState,
) => isActionInProgressSelector(state, "fetchDestinationCountries");

export const isFetchProductCategoriesInProgressSelector = (state: RootState) =>
  isActionInProgressSelector(state, "fetchProductCategories");

export const isFetchFormMasterDataInProgressSelector = (state: RootState) =>
  isActionInProgressSelector(state, "fetchFormMasterData");

export const isFetchCompanyDetailsInProgressSelector = (state: RootState) =>
  isActionInProgressSelector(state, "fetchCompanyDetails");

export const isFetchStatesInProgressSelector = (state: RootState) =>
  isActionInProgressSelector(state, "fetchStates");

export const { setLastFocusTermInfo } = onboardingSlice.actions;
export default onboardingSlice.reducer;
